/**
 * Imagen dentro de un mensaje de Gustavo (markdown `![…](url)`).
 *
 * (07/09/2026) El aviso del Studio IA manda "cómo quedó" como imagen en el texto. Sin
 * esto el `<img>` salía crudo, del ancho que trajera: en el widget tapaba la charla
 * entera. Miniatura chica y, al click, pantalla completa — no navegar a la URL, que
 * saca al agente del CRM.
 */
import { useState } from 'react';
import { Box, Dialog, IconButton, alpha } from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { CRM_PALETTE as P } from '../../../theme/brandPalette';

export const MINIATURA_MAX = 180;

/** Un video (el reel de la pieza para redes, 28/09/2026) llega con la misma sintaxis de imagen. */
export const esVideo = (src: string) => /\.(mp4|webm|mov)(\?|#|$)/i.test(src);

/** `max`: lado de la miniatura. Por defecto el del chat; el monitor la pide más chica. */
export function ImagenDelChat({ src, alt, max = MINIATURA_MAX }: { src?: string; alt?: string; max?: number }) {
  const [abierta, setAbierta] = useState(false);
  if (!src) return null;
  if (esVideo(src)) {
    // Vertical 9:16 con controles: se mira ahí mismo y se descarga con el link de al lado.
    return (
      <Box component="video" src={src} controls playsInline preload="metadata" aria-label={alt || 'Video'}
        sx={{ display: 'block', my: 0.75, width: max, aspectRatio: '9 / 16', borderRadius: 1.5, bgcolor: '#000',
          border: `1px solid ${alpha(P.brand.golden, 0.35)}` }} />
    );
  }
  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={() => setAbierta(true)}
        aria-label={alt ? `Ver en grande: ${alt}` : 'Ver imagen en grande'}
        sx={{
          display: 'block', p: 0, my: 0.75, cursor: 'zoom-in', background: 'none',
          border: `1px solid ${alpha(P.brand.golden, 0.35)}`, borderRadius: 1.5, overflow: 'hidden',
          maxWidth: max, lineHeight: 0,
          '&:focus-visible': { outline: `2px solid ${P.brand.golden}`, outlineOffset: 2 },
        }}
      >
        <Box component="img" src={src} alt={alt ?? ''} loading="lazy"
          sx={{ display: 'block', width: '100%', maxHeight: max, objectFit: 'cover' }} />
      </Box>
      <Dialog open={abierta} onClose={() => setAbierta(false)} maxWidth={false}
        PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none', m: 1, maxWidth: '96vw', maxHeight: '96vh', overflow: 'visible' } }}
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.88)' } } }}
      >
        <IconButton onClick={() => setAbierta(false)} aria-label="Cerrar"
          sx={{ position: 'absolute', top: -6, right: -6, zIndex: 1, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}>
          <CloseRounded />
        </IconButton>
        <Box component="img" src={src} alt={alt ?? ''} onClick={() => setAbierta(false)}
          sx={{ display: 'block', maxWidth: '96vw', maxHeight: '92vh', objectFit: 'contain', borderRadius: 1.5, cursor: 'zoom-out' }} />
      </Dialog>
    </>
  );
}
